import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { plans } from "@/config/stripePlans";
import {
  getCurrentCycleWindow,
  toCycleStartDateString,
} from "@/lib/macCycle";

export interface MacQuotaState {
  isLoading: boolean;
  isUnlimited: boolean;
  /** Resolved plan key. Lowercased subscription_tier. */
  planKey: string | null;
  /** MACs used in the current cycle (0 if no usage row yet). */
  macUsed: number;
  /** Plan's base MAC allowance. null = unlimited. */
  macBase: number | null;
  /** Hard ceiling on the plan. null = unlimited. */
  macCeiling: number | null;
  /** Effective cap = min(base + overage, ceiling). null = unlimited. */
  effectiveLimit: number | null;
  /** Sum of overage MACs purchased this cycle. */
  overagePurchased: number;
  /** True once macUsed >= effectiveLimit (and not unlimited). */
  isExhausted: boolean;
  /** End of current cycle = next reset. null until loaded. */
  cycleEnd: Date | null;
  /** Re-reads usage + overage from the DB (e.g. after an overage purchase). */
  refetch: () => void;
}

type MacQuotaData = Omit<MacQuotaState, "refetch">;

const DEFAULT_STATE: MacQuotaData = {
  isLoading: true,
  isUnlimited: false,
  planKey: null,
  macUsed: 0,
  macBase: null,
  macCeiling: null,
  effectiveLimit: null,
  overagePurchased: 0,
  isExhausted: false,
  cycleEnd: null,
};

/**
 * Reads the current MAC usage for a business and resolves it against the
 * business's plan limits + any overage purchases in the current cycle.
 * Replaces the legacy `ai_message_usage` quota read.
 */
export function useMacQuota(businessId: string | undefined | null): MacQuotaState {
  const [state, setState] = useState<MacQuotaData>(DEFAULT_STATE);
  const [reloadCounter, setReloadCounter] = useState(0);
  const refetch = useCallback(() => setReloadCounter((c) => c + 1), []);

  useEffect(() => {
    if (!businessId) {
      setState({ ...DEFAULT_STATE, isLoading: false });
      return;
    }

    let cancelled = false;

    (async () => {
      const { data: biz } = await supabase
        .from("businesses")
        .select("subscription_tier, billing_cycle_anchor")
        .eq("business_id", businessId)
        .maybeSingle();

      const planKey = (biz?.subscription_tier ?? "").toLowerCase();
      const plan = plans[planKey];

      if (!plan) {
        if (!cancelled) setState({ ...DEFAULT_STATE, isLoading: false, planKey });
        return;
      }

      const macBase = plan.limits.mac_base;
      const macCeiling = plan.limits.mac_ceiling;

      // Enterprise (mac_base null) → unlimited; skip usage read.
      if (macBase === null) {
        if (!cancelled) {
          setState({
            ...DEFAULT_STATE,
            isLoading: false,
            isUnlimited: true,
            planKey,
          });
        }
        return;
      }

      if (!biz?.billing_cycle_anchor) {
        if (!cancelled) {
          setState({
            ...DEFAULT_STATE,
            isLoading: false,
            planKey,
            macBase,
            macCeiling,
            effectiveLimit: macBase,
          });
        }
        return;
      }

      const { start, end } = getCurrentCycleWindow(new Date(biz.billing_cycle_anchor), new Date());
      const cycleStartStr = toCycleStartDateString(start);

      const [{ data: usage }, { data: overageRows }] = await Promise.all([
        supabase
          .from("monthly_mac_usage")
          .select("mac_count")
          .eq("business_id", businessId)
          .eq("cycle_start", cycleStartStr)
          .maybeSingle(),
        supabase
          .from("mac_overage_purchases")
          .select("macs_purchased")
          .eq("business_id", businessId)
          .eq("cycle_start", cycleStartStr),
      ]);

      const macUsed = (usage as { mac_count?: number } | null)?.mac_count ?? 0;
      const overagePurchased = (overageRows ?? []).reduce(
        (sum: number, row: { macs_purchased: number }) => sum + (row.macs_purchased ?? 0),
        0,
      );

      const effectiveLimit = Math.min(
        macBase + overagePurchased,
        macCeiling ?? Number.POSITIVE_INFINITY,
      );

      if (!cancelled) {
        setState({
          isLoading: false,
          isUnlimited: false,
          planKey,
          macUsed,
          macBase,
          macCeiling,
          effectiveLimit,
          overagePurchased,
          isExhausted: macUsed >= effectiveLimit,
          cycleEnd: end,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [businessId, reloadCounter]);

  return { ...state, refetch };
}
